import React, { useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import QuickAccess from '../../services/quick-access';
import { flattenMenu } from './flattenMenu';
import './styles.css';

// Icon phổ biến cho tab "Tùy chỉnh" — user bấm chọn, không phải gõ class FontAwesome.
export const ICON_CHOICES = [
    'fas fa-star', 'fas fa-bookmark', 'fas fa-bolt', 'fas fa-home',
    'fas fa-user', 'fas fa-users', 'fas fa-user-plus', 'fas fa-user-graduate',
    'fas fa-chalkboard-teacher', 'fas fa-graduation-cap', 'fas fa-school', 'fas fa-id-card',
    'fas fa-book', 'fas fa-book-open', 'fas fa-clipboard-list', 'fas fa-list',
    'fas fa-table', 'fas fa-calendar-alt', 'fas fa-clock', 'fas fa-bell',
    'fas fa-envelope', 'fas fa-file-alt', 'fas fa-folder', 'fas fa-plus',
    'fas fa-edit', 'fas fa-search', 'fas fa-cog', 'fas fa-chart-bar',
    'fas fa-chart-line', 'fas fa-money-bill-wave', 'fas fa-wallet', 'fas fa-credit-card',
    'fas fa-bus', 'fas fa-utensils', 'fas fa-print', 'fas fa-download',
];

// Chuẩn hóa đường dẫn nhập tay: nếu dán full URL trình duyệt (https://host/path)
// thì lấy phần path; đảm bảo có dấu "/" đầu để react-router điều hướng đúng.
export function toInternalPath(raw) {
    const v = (raw || '').trim();
    if (!v) return '';
    const m = v.match(/^https?:\/\/[^/]+(\/.*)?$/i);
    if (m) return m[1] || '/';
    return v.startsWith('/') ? v : '/' + v;
}

/**
 * Panel "Thêm mới Xem nhanh" dùng chung cho dropdown header lẫn trang dashboard.
 * 2 tab: "Từ menu" (chọn chức năng có trong sidebar) + "Tùy chỉnh" (tự nhập
 * nhãn/đường dẫn + bấm chọn icon). Gọi onCreated(item) khi thêm thành công.
 */
export default function QuickAccessAddPanel({ onCreated }) {
    const menuOptions = useMemo(() => flattenMenu(), []);
    const [tab, setTab] = useState('menu'); // 'menu' | 'custom'
    const [search, setSearch] = useState('');
    const [picked, setPicked] = useState(null);
    const [labelInput, setLabelInput] = useState('');
    const [customLabel, setCustomLabel] = useState('');
    const [customUrl, setCustomUrl] = useState('');
    const [customIcon, setCustomIcon] = useState('');
    const [saving, setSaving] = useState(false);

    const filteredOptions = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return menuOptions;
        return menuOptions.filter(
            (o) =>
                o.label.toLowerCase().includes(q) ||
                (o.group && o.group.toLowerCase().includes(q))
        );
    }, [menuOptions, search]);

    const pickOption = (opt) => {
        setPicked(opt);
        setLabelInput(opt.label);
    };

    const create = (payload) => {
        setSaving(true);
        QuickAccess.createQuickAccess(payload, (res) => {
            setSaving(false);
            if (res && res.success && res.data) {
                toast.success('Đã thêm vào Xem nhanh');
                onCreated && onCreated(res.data);
            } else {
                toast.error((res && res.message) || 'Không thêm được shortcut');
            }
        });
    };

    const saveMenu = () => {
        if (!picked) return;
        const label = (labelInput || '').trim() || picked.label;
        create({ label, url: picked.url, icon: picked.icon || '' });
    };

    const saveCustom = () => {
        const label = (customLabel || '').trim();
        const url = toInternalPath(customUrl);
        if (!label) { toast.warn('Vui lòng nhập nhãn'); return; }
        if (!url) { toast.warn('Vui lòng nhập đường dẫn'); return; }
        create({ label, url, icon: (customIcon || '').trim() || 'fas fa-star' });
    };

    return (
        <div className="qa-body">
            <div className="qa-tabs">
                <button type="button" className={tab === 'menu' ? 'active' : ''}
                    onClick={() => { setTab('menu'); setPicked(null); }}>Từ menu</button>
                <button type="button" className={tab === 'custom' ? 'active' : ''}
                    onClick={() => setTab('custom')}>Tùy chỉnh</button>
            </div>

            {tab === 'menu' && (!picked ? (
                <>
                    <input
                        className="qa-search"
                        type="text"
                        placeholder="Tìm chức năng…"
                        value={search}
                        autoFocus
                        onChange={(e) => setSearch(e.target.value)}
                    />
                    <div className="qa-option-list">
                        {filteredOptions.length === 0 && (
                            <div className="qa-empty">Không tìm thấy chức năng phù hợp.</div>
                        )}
                        {filteredOptions.map((opt) => (
                            <div className="qa-option" key={opt.url} onClick={() => pickOption(opt)}>
                                <span className="qa-icon sm"><i className={opt.icon} /></span>
                                <span className="qa-option-text">
                                    <span className="qa-option-label">{opt.label}</span>
                                    {opt.group && <span className="qa-option-group">{opt.group}</span>}
                                </span>
                            </div>
                        ))}
                    </div>
                </>
            ) : (
                <div className="qa-confirm">
                    <div className="qa-confirm-preview">
                        <span className="qa-icon"><i className={picked.icon} /></span>
                        <span className="qa-muted">{picked.group ? picked.group + ' › ' : ''}{picked.label}</span>
                    </div>
                    <label className="qa-field-label">Nhãn hiển thị</label>
                    <input
                        className="qa-search"
                        type="text"
                        value={labelInput}
                        maxLength={100}
                        autoFocus
                        onChange={(e) => setLabelInput(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') saveMenu(); }}
                    />
                    <div className="qa-confirm-actions">
                        <button type="button" className="qa-btn ghost" onClick={() => setPicked(null)}>Chọn lại</button>
                        <button type="button" className="qa-btn primary" disabled={saving} onClick={saveMenu}>
                            {saving ? 'Đang lưu…' : 'Thêm'}
                        </button>
                    </div>
                </div>
            ))}

            {tab === 'custom' && (
                <div className="qa-custom">
                    <label className="qa-field-label">Nhãn hiển thị</label>
                    <input
                        className="qa-search"
                        type="text"
                        placeholder="VD: Thêm mới học sinh"
                        value={customLabel}
                        maxLength={100}
                        onChange={(e) => setCustomLabel(e.target.value)}
                    />
                    <label className="qa-field-label">Đường dẫn</label>
                    <input
                        className="qa-search"
                        type="text"
                        placeholder="/ad-sas-school/... hoặc dán link trình duyệt"
                        value={customUrl}
                        onChange={(e) => setCustomUrl(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') saveCustom(); }}
                    />
                    <div className="qa-hint">
                        Mẹo: mở trang cần ghim rồi copy đường dẫn trên thanh địa chỉ trình duyệt (phần sau tên miền).
                    </div>
                    <label className="qa-field-label">Chọn icon</label>
                    <div className="qa-icon-grid">
                        {ICON_CHOICES.map((ic) => {
                            const active = ((customIcon || '').trim() || 'fas fa-star') === ic;
                            return (
                                <button
                                    type="button"
                                    key={ic}
                                    className={`qa-icon-choice ${active ? 'active' : ''}`}
                                    title={ic.replace('fas fa-', '')}
                                    onClick={() => setCustomIcon(ic)}
                                >
                                    <i className={ic} />
                                </button>
                            );
                        })}
                    </div>
                    <div className="qa-confirm-actions">
                        <button type="button" className="qa-btn primary" disabled={saving} onClick={saveCustom}>
                            {saving ? 'Đang lưu…' : 'Thêm'}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
